import { Box, Spinner } from '@factorialco/deprecated-design-system'
import {
  ContractsContractPoliciesFragment,
  ContractsCustomFieldsPoliciesFragment,
  ContractsEmployeesContractVersionFragment,
  ContractsEmployeesEmployeeFragment,
  ContractsEmployeesJobCatalogContextlessPoliciesFragment,
  ContractsEmployeesReferenceContractFragment,
  ContractsRequestUpdateFlowDocument,
  JobCatalogTreeFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'

import { useContractFlow } from 'modules/contracts/lib/contractFlowContext'

import UpdateFlowContent from './_UpdateFlowContent'

const Loading = () => {
  return (
    <Box width="full" height="full" justifyContent="center" alignItems="center">
      <Spinner />
    </Box>
  )
}

interface Props {
  contractVersion: ContractsEmployeesReferenceContractFragment | null
  employee: ContractsEmployeesEmployeeFragment
  jobCatalogTrees?: JobCatalogTreeFragment[]
  jobCatalogTreeFeatureEnabled?: boolean
  contractContextlessPolicies: ContractsContractPoliciesFragment
  jobCatalogContextlessPolicies: ContractsEmployeesJobCatalogContextlessPoliciesFragment | undefined
  hasDevTimeTrackingWorkableDaysDefinition: boolean
  hasTimeTrackingAnnualisationDailyValue: boolean
  contractVersionPayrollPolicyId?: number
  hasPayrollBundle: boolean
  customFieldsContextlessPolicies: ContractsCustomFieldsPoliciesFragment | undefined
  contractVersions: ContractsEmployeesContractVersionFragment[]
}

const UpdateFlow = ({
  contractVersion,
  employee,
  jobCatalogTrees,
  jobCatalogTreeFeatureEnabled,
  contractContextlessPolicies,
  jobCatalogContextlessPolicies,
  hasDevTimeTrackingWorkableDaysDefinition,
  hasTimeTrackingAnnualisationDailyValue,
  contractVersionPayrollPolicyId,
  hasPayrollBundle,
  customFieldsContextlessPolicies,
  contractVersions,
}: Props) => {
  const { setCustomFields, setCustomValues } = useContractFlow()
  const { data, loading } = useQuery(ContractsRequestUpdateFlowDocument, {
    variables: {
      contractVersionId: contractVersion?.id ?? 0,
      hasContractVersion: !!contractVersion,
      legalEntityId: contractVersion?.employee?.legalEntity?.id ?? 0,
    },
  })
  const customFields = (
    data?.customFields.resourceFieldsConnection.edges.map((edge) => edge.node.field) ?? []
  ).filter(Boolean)
  const customValues = data?.contracts?.contractVersion?.values ?? []

  useEffect(() => {
    if (!loading) {
      setCustomFields(customFields)
      setCustomValues(customValues)
    }
  }, [loading])

  if (loading) return <Loading />

  return (
    <UpdateFlowContent
      customFields={customFields}
      customValues={customValues}
      jobCatalogTrees={jobCatalogTrees}
      jobCatalogTreeFeatureEnabled={jobCatalogTreeFeatureEnabled}
      contractVersion={contractVersion}
      contractVersions={contractVersions}
      contractVersionPayrollPolicyId={contractVersionPayrollPolicyId}
      employee={employee}
      contractContextlessPolicies={contractContextlessPolicies}
      hasDevTimeTrackingWorkableDaysDefinition={hasDevTimeTrackingWorkableDaysDefinition}
      hasTimeTrackingAnnualisationDailyValue={hasTimeTrackingAnnualisationDailyValue}
      jobCatalogContextlessPolicies={jobCatalogContextlessPolicies}
      hasPayrollBundle={hasPayrollBundle}
      customFieldsContextlessPolicies={customFieldsContextlessPolicies}
    />
  )
}

export default observer(UpdateFlow)
